import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CourseProgress, TrainingAttestation, TrainingOrgProgress } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth, useOrg } from '../../lib/auth.js';
import {
  Button,
  EmptyState,
  LoadingPane,
  PageHeader,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { AttestationDialog } from './AttestationDialog.js';
import './training.css';

/** Tooltip / screen-reader text for a completed cell, naming the provenance. */
function completionText(c: CourseProgress): string {
  if (c.attested && c.attestation) {
    return `Attested completion – ${c.attestation.source} · recorded by ${
      c.attestation.attestedByName ?? 'an admin'
    } on ${formatDate(c.attestation.attestedAt)} · expires ${formatDate(c.expiresAt)}`;
  }
  return `Completed ${formatDate(c.completedAt)} · expires ${formatDate(c.expiresAt)}`;
}

export function TrackTrainingPage() {
  const { me } = useAuth();
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = org.role === 'admin' || (me?.user.isSystemAdmin ?? false);
  const canView = isAdmin || org.role === 'reviewer';

  const progress = useQuery({
    queryKey: ['training-org-progress', org.orgId],
    queryFn: () => apiFetch<TrainingOrgProgress>(`/orgs/${org.orgId}/training/progress`),
    enabled: canView,
  });
  const attestations = useQuery({
    queryKey: ['training-attestations', org.orgId],
    queryFn: () =>
      apiFetch<{ items: TrainingAttestation[] }>(`/orgs/${org.orgId}/training/attestations`),
    enabled: canView,
  });

  const [recordOpen, setRecordOpen] = useState(false);
  const revoke = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/orgs/${org.orgId}/training/attestations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Attestation revoked.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['training-attestations', org.orgId] });
      void queryClient.invalidateQueries({ queryKey: ['training-org-progress', org.orgId] });
      void queryClient.invalidateQueries({ queryKey: ['training-progress', org.orgId] });
      void queryClient.invalidateQueries({ queryKey: ['training-courses', org.orgId] });
      void queryClient.invalidateQueries({ queryKey: ['courses', org.orgId] });
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not revoke the attestation', 'error'),
  });

  if (!canView) {
    return (
      <EmptyState
        title="Not available"
        message="Track Training is available to organisation admins and reviewers."
      />
    );
  }
  if (progress.isLoading) return <LoadingPane label="Loading training progress" />;
  if (progress.isError || !progress.data) {
    return (
      <div>
        <PageHeader title="Track Training" />
        <EmptyState
          title="Could not load training progress"
          message="Something went wrong fetching the organisation's progress — please try again shortly."
        />
      </div>
    );
  }
  const data = progress.data;
  const attestationItems = attestations.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Track Training"
        subtitle="Organisation-wide view of course progress for every member. Completions are valid for 12 months."
        actions={
          isAdmin ? (
            <Button variant="secondary" onClick={() => setRecordOpen(true)}>
              Record prior completion
            </Button>
          ) : undefined
        }
      />
      {data.members.length === 0 ? (
        <EmptyState title="No members" message="Invite members to start tracking training." />
      ) : (
        <div className="track-table-wrap">
          <table className="track-table">
            <thead>
              <tr>
                {/* Sticky header cells are sibling stacking contexts that
                    paint in DOM order — descending z-index keeps each rotated
                    title above the cells it crosses; the corner stays top. */}
                <th
                  scope="col"
                  className="track-member-col"
                  style={{ zIndex: data.courses.length + 3 }}
                >
                  Member
                </th>
                {data.courses.map((course, i) => (
                  <th
                    key={course.id}
                    scope="col"
                    className="track-course-head"
                    title={course.title}
                    style={{ zIndex: data.courses.length + 1 - i }}
                  >
                    <span className="track-course-title">{course.title}</span>
                  </th>
                ))}
                <th scope="col" style={{ verticalAlign: 'bottom', zIndex: 1 }}>
                  Completed
                </th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((member) => {
                const completed = member.courses.filter(
                  (c) => c.status === 'completed' && !c.expired,
                ).length;
                return (
                  <tr key={member.userId}>
                    <th scope="row" className="track-member-col">
                      <div className="track-member-name">{member.name || member.email}</div>
                      <div className="track-member-role">{member.role}</div>
                    </th>
                    {member.courses.map((c) => (
                      <td key={c.courseId}>
                        {c.status === 'completed' && !c.expired ? (
                          <span
                            className={`track-cell track-done${c.attested ? ' track-attested' : ''}`}
                            title={completionText(c)}
                          >
                            <span aria-hidden="true">{c.attested ? 'A' : '✓'}</span>
                            <span className="sr-only">{completionText(c)}</span>
                          </span>
                        ) : c.expired ? (
                          <span
                            className="track-cell track-expired"
                            title={`Expired ${formatDate(c.expiresAt)} — needs retaking${
                              c.attested ? ' (attested completion)' : ''
                            }`}
                          >
                            <span aria-hidden="true">!</span>
                            <span className="sr-only">
                              Expired {formatDate(c.expiresAt)}, needs retaking
                              {c.attested ? ' (attested completion)' : ''}
                            </span>
                          </span>
                        ) : c.status === 'in_progress' ? (
                          <span className="track-cell track-progress" title={`${c.percent}% complete`}>
                            {c.percent}%<span className="sr-only"> complete</span>
                          </span>
                        ) : (
                          <span className="track-cell track-none" title="Not started">
                            <span aria-hidden="true">–</span>
                            <span className="sr-only">Not started</span>
                          </span>
                        )}
                      </td>
                    ))}
                    <td>
                      <strong>
                        {completed}/{data.courses.length}
                      </strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="track-legend">
        <span className="track-cell track-done">✓</span> completed ·{' '}
        <span className="track-cell track-done track-attested">A</span> attested completion ·{' '}
        <span className="track-cell track-progress">%</span> in progress ·{' '}
        <span className="track-cell track-expired">!</span> expired ·{' '}
        <span className="track-cell track-none">–</span> not started
      </p>

      <details className="track-attestations">
        <summary>Attestations ({attestationItems.length})</summary>
        <p className="track-attestations-note">
          Prior completions recorded by an admin. Revoking one removes the completion until the
          person passes the final quiz; people with a pending invitation are linked when they join.
        </p>
        {attestationItems.length === 0 ? (
          <p className="track-attestations-note">No attestations recorded.</p>
        ) : (
          <Table
            head={
              <>
                <th>Person</th>
                <th>Course</th>
                <th>Completed</th>
                <th>Expires</th>
                <th>Source</th>
                <th>Recorded by</th>
                {isAdmin && <th />}
              </>
            }
          >
            {attestationItems.map((a) => (
              <tr key={a.id}>
                <td>
                  {a.userName ?? a.email}
                  {a.userName === null && (
                    <div className="track-attestation-meta">pending invitation</div>
                  )}
                  {a.userName !== null && (
                    <div className="track-attestation-meta">{a.email}</div>
                  )}
                </td>
                <td>{a.courseTitle}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(a.completedAt)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(a.expiresAt)}</td>
                <td>{a.source}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {a.attestedByName ?? '—'}
                  <div className="track-attestation-meta">{formatDate(a.attestedAt)}</div>
                </td>
                {isAdmin && (
                  <td>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={revoke.isPending && revoke.variables === a.id}
                      onClick={() => revoke.mutate(a.id)}
                    >
                      Revoke
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </Table>
        )}
      </details>

      {isAdmin && (
        <AttestationDialog
          courses={data.courses}
          open={recordOpen}
          onClose={() => setRecordOpen(false)}
        />
      )}
    </div>
  );
}
